import { Extension } from "@tiptap/core";
import type { Editor, Range } from "@tiptap/core";
import { ReactRenderer } from "@tiptap/react";
import { Suggestion } from "@tiptap/suggestion";
import { PluginKey } from "@tiptap/pm/state";

import { FieldMenu, type FieldMenuHandle } from "./field-menu";
import type { EditorField } from "./fields-context";

export interface CreateFieldContext {
	editor: Editor;
	range: Range;
	query: string;
}

interface FieldsSuggestionOptions {
	getFields: (editor: Editor) => EditorField[];
	onCreateField?: (context: CreateFieldContext) => void;
}

export const fieldsSuggestionKey = new PluginKey("fieldsSuggestion");

export const FieldsSuggestion = Extension.create<FieldsSuggestionOptions>({
	name: "fieldsSuggestion",

	addOptions() {
		return { getFields: () => [] };
	},

	addProseMirrorPlugins() {
		return [
			Suggestion<EditorField>({
				editor: this.editor,
				pluginKey: fieldsSuggestionKey,
				char: "/",
				items: ({ query }) =>
					this.options
						.getFields(this.editor)
						.filter((field) => field.label.toLowerCase().includes(query.toLowerCase())),
				command: ({ editor, range, props }) => {
					editor
						.chain()
						.focus()
						.insertContentAt(range, { type: "field", attrs: { fieldId: props.id } })
						.run();
				},
				render: () => {
					let component: ReactRenderer<FieldMenuHandle> | null = null;
					let unmount: (() => void) | undefined;

					const menuProps = (props: {
						items: EditorField[];
						command: (item: EditorField) => void;
						editor: Editor;
						range: Range;
						query: string;
					}) => ({
						items: props.items,
						command: props.command,
						query: props.query,
						onCreate:
							this.options.onCreateField === undefined
								? undefined
								: () =>
										this.options.onCreateField?.({
											editor: props.editor,
											range: props.range,
											query: props.query,
										}),
					});

					return {
						onStart(props) {
							component = new ReactRenderer(FieldMenu, {
								props: menuProps(props),
								editor: props.editor,
							});

							unmount = props.mount(component.element);
						},
						onUpdate(props) {
							component?.updateProps(menuProps(props));
						},
						onKeyDown(props) {
							if (props.event.key === "Escape") {
								return false;
							}

							return component?.ref?.onKeyDown(props.event) ?? false;
						},
						onExit() {
							unmount?.();
							component?.destroy();
							component = null;
						},
					};
				},
			}),
		];
	},
});
